<template>
  <div>
    <div v-if="parsedAvailabilities.length" class="my-5">
      <div class="products-list">
        <div
          v-for="room in parsedAvailabilities"
          :key="room.id"
          class="mx-0 products-list-item my-5"
        >
          <div class="row p-md-3 px-0 py-3">
            <div class="col-md-5">
              <div class="position-relative">
                <b-carousel
                  id="carousel-fade"
                  :interval="3000"
                  style="text-shadow: 0px 0px 2px #000"
                  fade
                  indicators
                  img-width="1024"
                  img-height="480"
                  ><div
                    class="cursor-pointer"
                    @click="$emit('openDetails', room.images)"
                  >
                    <template v-if="room.images.length">
                      <div v-for="url of room.images" :key="url">
                        <b-carousel-slide
                          style="max-height: 250px"
                          :img-src="url"
                        ></b-carousel-slide>
                      </div>
                    </template>
                    <template v-else>
                      <b-carousel-slide
                        v-for="i of 1"
                        :key="i"
                        img-src="/images/property-default-image.jpg"
                      ></b-carousel-slide>
                    </template>
                  </div>
                </b-carousel>
              </div>
            </div>
            <div class="col-md-7">
              <h4
                class="mb-md-3 mb-1 text-main text-capitalize font-weight-bold mt-md-0 mt-3"
              >
                {{ room.name }}
              </h4>
            </div>
          </div>
          <div
            v-for="product in room.products"
            :key="product.id"
            class="selectors p-md-3 px-0 py-2"
          >
            <div class="row">
              <div class="col-md-5">
                <div
                  class="small"
                  :class="`text-${
                    product.rate.mealIncluded ? 'success' : 'danger'
                  }`"
                >
                  <img
                    v-if="product.rate.mealIncluded"
                    src="../../assets/icons/meal-success.svg"
                    class="mr-1"
                    alt="meal"
                  />
                  <img
                    v-else
                    src="../../assets/icons/meal.svg"
                    class="mr-1"
                    alt="meal"
                  />
                  <span>{{
                    product.rate.mealIncluded
                      ? $t("reservation.mealIncluded")
                      : $t("property.noMealIncluded")
                  }}</span>
                </div>
                <div class="mt-2 font-weight-bold">
                  {{ product.ratesCount > 1 ? `${product.ratesCount}x` : "" }}
                  {{ rateDetails(product) }}
                </div>
                <div class="small text-main mt-2">
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="rateDetailsText(product)"
                    >{{ $t("property.rateDetails") }}</span
                  >
                  |
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      cancellationPolicyText(product)
                    "
                    >{{ $t("property.cancellationPolicy") }}</span
                  >
                </div>
              </div>
              <div class="col-md-3">
                <div class="h-100 d-md-flex d-block">
                  <div class="my-md-auto my-1">
                    <GuestIcons :persons="product.rate.totalGuests" />
                    <span class="ml-1 text-14">{{
                      $t("property.persons")
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div
                  class="h-100 mt-md-auto mt-0 d-flex align-items-center justify-content-between"
                >
                  <div>
                    <h3 class="text-main font-weight-bold actual-amount">
                      {{ product.currency }}
                      {{ product.amount.toFixed(2) | formatPrice }}
                    </h3>
                  </div>
                  <div>
                    <button
                      @click="bookNow(product)"
                      class="btn px-4 g-primary-button"
                    >
                      {{ $t("property.bookNow") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="fetched && !availabilities.length"
      class="text-danger no-availability py-3 my-5 text-center"
    >
      <h5 class="mb-0">
        <b-icon icon="exclamation-circle" class="text-danger"></b-icon>
        <span class="ml-2">
          <span v-html="noAvailabilityMessage"></span>
        </span>
      </h5>
    </div>
    <div v-else-if="!fetched" class="py-5 mt-5 text-center text-md">
      <h5>{{ $t("property.selectStay") }}</h5>
    </div>
  </div>
</template>

<script>
import {
  rateDetails,
  rateDetailsText,
  cancellationPolicyText,
} from "../../helpers/nuitee";
import { mapState } from "vuex";
import GuestIcons from "../Widgets/GuestIcons.vue";
import {
  getNoAvailabilityMessage,
  getPropertyPrimaryEmail,
} from "../../helpers/theme";

export default {
  name: "NuiteeList",
  components: { GuestIcons },
  props: {
    property: {
      type: Object,
      required: true,
    },
    availabilities: {
      type: Array,
      required: true,
    },
    fetched: {
      type: Boolean,
      default: false,
    },
    checkIn: {
      type: String,
      default: null,
    },
    checkOut: {
      type: String,
      default: null,
    },
  },
  data() {
    return {};
  },
  computed: {
    ...mapState("property", ["availabiltyDefaultMedia"]),
    noAvailabilityMessage() {
      return getNoAvailabilityMessage({
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        hotelEmail: getPropertyPrimaryEmail(this.property),
      });
    },
    parsedAvailabilities() {
      return this.availabilities.map((availability) => {
        if (
          !availability.images.length &&
          this.availabiltyDefaultMedia.length
        ) {
          availability.images = this.availabiltyDefaultMedia;
        }
        return availability;
      });
    },
  },
  methods: {
    rateDetails,
    rateDetailsText,
    cancellationPolicyText,
    bookNow(product) {
      this.$emit("bookNow", {
        product,
        id: product.id,
        guests: product.rate.totalGuests,
        price: product.amount / product.ratesCount,
        quantity: product.ratesCount,
        currencyCode: product.currency,
      });
    },
  },
};
</script>

<style>
.text-14 {
  font-size: 14px;
}
.actual-amount {
  font-size: 25px;
}

@media screen and (max-width: 600px) {
  .actual-amount {
    font-size: 18px;
    margin-bottom: 0;
  }
  .text-14 {
    font-size: 12px;
  }
}
</style>
